import { AlertTriangle, CheckCircle, Clock, Database, HelpCircle, Server, XCircle } from 'lucide-react';
import { Card, CardHeader } from '../../components/Card';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { timeAgo } from '../../lib/format';
import { useSystemHealth, type SystemHealthService } from '../../api/admin';

const STATUS_META: Record<SystemHealthService['status'], { label: string; icon: typeof CheckCircle; chip: string; text: string }> = {
  ok: {
    label: 'Healthy',
    icon: CheckCircle,
    chip: 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
  degraded: {
    label: 'Degraded',
    icon: AlertTriangle,
    chip: 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300',
    text: 'text-amber-700 dark:text-amber-300',
  },
  down: {
    label: 'Down',
    icon: XCircle,
    chip: 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300',
    text: 'text-rose-700 dark:text-rose-300',
  },
  unknown: {
    label: 'Not monitored',
    icon: HelpCircle,
    chip: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400',
    text: 'text-zinc-500 dark:text-zinc-400',
  },
};

/** System health — LIVE checks (PG, MariaDB, outbox, webhooks, billing cron) + queue depth + DB distribution. */
export function SystemHealth() {
  const { data: systemHealth, isLoading } = useSystemHealth();

  if (isLoading || !systemHealth) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <Skeleton className="h-8 w-64 rounded" />
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-32 rounded-lg" />
      </div>
    );
  }

  const dist = systemHealth.databaseDistribution;
  const totalDbs = dist.shared + dist.managed + dist.selfHosted + dist.legacy;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="System health"
        subtitle="Live checks against the services this deployment actually runs."
        action={
          systemHealth.allNominal ? (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              All systems nominal
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Attention needed
            </span>
          )
        }
      />

      <Card className="p-5 sm:p-6 mb-4">
        <CardHeader title="Services" subtitle={`${systemHealth.services.length} services checked live on load`} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {systemHealth.services.map((svc) => {
            const meta = STATUS_META[svc.status] ?? STATUS_META.unknown;
            const Icon = meta.icon;
            return (
              <div key={svc.name} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
                <span className={cn('w-9 h-9 grid place-items-center rounded-md', meta.chip)}>
                  <Server className="w-4 h-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{svc.name}</div>
                  <div className={cn('text-xs flex items-center gap-1.5', meta.text)}>
                    <Icon className="w-3 h-3" />
                    {meta.label}
                    <span className="text-zinc-500 dark:text-zinc-400">· {svc.detail}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-3 sm:gap-4 mb-4">
        <Card className="p-5 sm:p-6">
          <CardHeader title="Queue depth" subtitle="Background jobs waiting to be processed" />
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(systemHealth.queueDepth).map(([name, n]) => (
              <div key={name} className="rounded-md p-3 bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
                <div className={cn(
                  'text-2xl font-semibold tabular-nums',
                  n === 0 ? 'text-zinc-400 dark:text-zinc-500' : n < 10 ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300',
                )}>
                  {n}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 capitalize">{name}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Billing cron" subtitle="Nightly renewals + dunning cycle (02:00 Asia/Dhaka)" />
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
              <Clock className="w-4 h-4" />
            </span>
            <div>
              <div className="text-lg font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">
                {systemHealth.billingCronLastRunAt ? timeAgo(systemHealth.billingCronLastRunAt) : 'No run recorded yet'}
              </div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                {systemHealth.billingCronLastRunAt
                  ? `last completed run · ${new Date(systemHealth.billingCronLastRunAt).toLocaleString()}`
                  : 'The heartbeat is written at the end of each nightly cycle.'}
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Business databases" subtitle={`${totalDbs} total, by database mode`} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <DbStat label="Shared" n={dist.shared} tone="info" description="New businesses in the shared POS database" />
          <DbStat label="Managed" n={dist.managed} tone="emerald" description="Provisioned by us, isolated per business" />
          <DbStat label="Self-hosted" n={dist.selfHosted} tone="info" description="Customer-managed, accessed via TLS" />
          <DbStat label="Legacy (shared)" n={dist.legacy} tone="amber" description="Grandfathered into the original deployment DBs" />
        </div>
      </Card>
    </div>
  );
}

function DbStat({
  label,
  n,
  tone,
  description,
}: {
  label: string;
  n: number;
  tone: 'emerald' | 'info' | 'amber';
  description: string;
}) {
  const cls = {
    emerald: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900',
    info: 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900',
    amber: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900',
  }[tone];
  return (
    <div className={cn('rounded-lg p-4 border', cls)}>
      <div className="flex items-center gap-2 mb-2">
        <Database className="w-3.5 h-3.5 opacity-80" />
        <span className="text-xs uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-semibold tabular-nums">{n}</div>
      <div className="text-xs mt-1 opacity-80">{description}</div>
    </div>
  );
}
